import React from 'react';

import type { ConfirmDialogProps } from '@lumapps/lumx-dialogs/components/ConfirmDialog';
import { FlexBox, Orientation, Size, Text, Typography } from '@lumapps/lumx/react';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useSelector } from '@lumapps/redux/react';
import { useRouter } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { wrapStringGroupWithComponent } from '@lumapps/utils/string/wrapStringGroupWithComponent';

import { unarchive, archive } from '../api/contents';
import { getCurrentCustomContentTypeId } from '../ducks/selectors';
import { CONTENTS } from '../keys';
import { adminContent } from '../routes';
import type { Content } from '../types';

export const useContentArchive = ({
    openConfirmDialog,
    contentId,
    contentTitle = '',
    onSuccess,
}: {
    contentId?: string;
    contentTitle: string;
    openConfirmDialog: (
        props: Pick<ConfirmDialogProps, 'title' | 'confirmMessage' | 'onConfirm' | 'confirmProps' | 'id'>,
    ) => void;
    onSuccess: (content: Content) => void;
}) => {
    const { success, error } = useNotification();
    const { translateKey, translateAndReplace, pluralize } = useTranslate();
    const { redirect } = useRouter();
    const contentTypeId = useSelector(getCurrentCustomContentTypeId);

    const archiveContent = React.useCallback(async () => {
        if (!contentId) {
            return;
        }
        try {
            const { data: updatedContent } = await archive(contentId);
            success({ content: translateKey(CONTENTS.ARCHIVE_SUCCESS) });
            onSuccess(updatedContent);
            if (contentTypeId) {
                redirect(adminContent(contentTypeId));
            }
        } catch (err) {
            error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
        }
    }, [contentTypeId, contentId, error, onSuccess, redirect, success, translateKey]);

    const unarchiveContent = React.useCallback(async () => {
        if (!contentId) {
            return;
        }
        try {
            const { data: updatedContent } = await unarchive(contentId);
            success({ content: translateKey(CONTENTS.UNARCHIVE_SUCCESS) });
            onSuccess(updatedContent);
        } catch (err) {
            error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
        }
    }, [contentId, error, onSuccess, success, translateKey]);

    const onArchive = React.useCallback(() => {
        openConfirmDialog({
            title: translateKey(CONTENTS.CONFIRM_ARCHIVE),
            confirmMessage: (
                <FlexBox orientation={Orientation.vertical} gap={Size.big}>
                    <Text as="p" typography={Typography.body1}>
                        {translateKey(CONTENTS.EXPLAIN_ARCHIVE)}
                    </Text>
                    <Text as="p" typography={Typography.body1}>
                        {pluralize(CONTENTS.TO_REVERSE_ARCHIVE, 1)}
                    </Text>
                </FlexBox>
            ),
            onConfirm: archiveContent,
            confirmProps: { label: translateKey(GLOBAL.ARCHIVE) },
            id: 'archive',
        });
    }, [archiveContent, openConfirmDialog, pluralize, translateKey]);

    const onUnarchive = React.useCallback(() => {
        openConfirmDialog({
            title: translateKey(CONTENTS.CONFIRM_UNARCHIVE),
            confirmMessage: (
                <Text as="p" typography={Typography.body1}>
                    {wrapStringGroupWithComponent(
                        translateAndReplace(CONTENTS.EXPLAIN_UNARCHIVE_ONE, {
                            CONTENT_NAME: contentTitle,
                        }),
                        {
                            start: '<strong>',
                            end: '</strong>',
                        },
                        (value) => (
                            <strong>{value}</strong>
                        ),
                    )}
                </Text>
            ),
            onConfirm: unarchiveContent,
            confirmProps: { label: translateKey(GLOBAL.UNARCHIVE) },
            id: 'unarchive',
        });
    }, [contentTitle, openConfirmDialog, translateAndReplace, translateKey, unarchiveContent]);

    return {
        onArchive,
        onUnarchive,
    };
};
